import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight, PiShieldWarningLight } from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import { GRID_COLS_CLASS } from '../../shared/FormComponents/FormSection';
import FormActions from '../../shared/FormComponents/FormActions';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import type { SkeletonVariant } from '../../shared/TailwindComponents/Skeleton';
import type { SettingsRequirement, SettingsViewer } from './settingsRegistry';

/**
 * The chrome every settings section wears.
 *
 * A section body decides WHAT it edits; this decides how it announces itself
 * (title/description/icon), how it says it is loading, and how it commits —
 * the three things that were being re-invented per tab.
 *
 * Existing sections do not have to adopt it yet: phase 1 renders them
 * unchanged inside `SettingsLayout`. New and restyled sections should compose
 * this so the save affordance is the same everywhere.
 *
 * Save modes:
 *   explicit - a Save button, enabled only while `dirty`. The default.
 *   instant  - no button; each control writes on change, and the header shows a
 *              transient "Saved" once `dirty` goes false after having been true.
 *   batch    - Save + Discard, with a count of pending edits ("3 unsaved changes").
 */

export type SettingsSaveMode = 'explicit' | 'instant' | 'batch';

export interface SettingsSectionProps {
  /**
   * Already-translated group title. Leave it (and `description`) off when the
   * section is the only one on its page: the page header already says it.
   */
  title?: React.ReactNode;
  /** Already-translated muted line under the title. */
  description?: string;
  /**
   * @deprecated Not rendered. The page header carries the one glyph a settings
   * page has; groups inside it are text only, on every page alike.
   */
  icon?: React.ReactNode;
  /** How this section commits. Default 'explicit'. */
  saveMode?: SettingsSaveMode;
  /** Swap the body for a skeleton. */
  isLoading?: boolean;
  /** Shape of that skeleton. Default 'form'. */
  loadingVariant?: SkeletonVariant;
  /** There are unsaved edits. Drives the footer and the "Saved" affordance. */
  dirty?: boolean;
  /** Number of pending edits, shown by `saveMode="batch"`. */
  pendingCount?: number;
  isSaving?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  saveLabel?: string;
  /** Left-hand slot of the footer row (a destructive action, a hint). */
  footerLeading?: React.ReactNode;
  /** Extra buttons/links in the header row. */
  actions?: React.ReactNode;
  /**
   * Same shape the registry uses. When given together with `viewer` and not
   * met, the section renders its not-authorized state instead of the body.
   */
  requires?: SettingsRequirement;
  viewer?: SettingsViewer;
  /** Renders the body as-is instead of in FormSection's field grid. Default true. */
  noGrid?: boolean;
  /**
   * OPT-IN: this section's body is a LIST, so it fills the settings pane
   * instead of being sized by its content - the table grows with the screen
   * and its pager sits at the bottom of the pane rather than mid-canvas.
   *
   * Carries `fill` down through `FormSection` to `GlassCard`, which is the
   * whole point: a `h-full` list inside a content-sized card resolves against
   * `auto` and grows to all of its rows with nothing scrolling. The body is
   * expected to own its scroller - `LIST_SECTION_SHELL` is the shape for it.
   * Field forms leave this off.
   */
  fill?: boolean;
  /** Field-grid columns when `noGrid` is false. */
  gridCols?: 1 | 2 | 3 | 4;
  className?: string;
  children: React.ReactNode;
}

const SAVED_VISIBLE_MS = 2000;

/**
 * THE settings type scale. A page has exactly three levels of heading and they
 * are these - page (`SettingsSectionHeader`), group (a section's own title)
 * and subsection (`SettingsSubsection`). Field labels and help lines come from
 * FieldShell. `settingsStyleGuard.test.ts` keeps section bodies from inventing
 * a fourth.
 */
export const SETTINGS_PAGE_TITLE = 'text-lg font-medium text-fg-strong';
export const SETTINGS_PAGE_DESCRIPTION = 'mt-1.5 text-sm text-fg-muted';
export const SETTINGS_GROUP_TITLE = 'text-base font-medium text-fg-strong';
export const SETTINGS_GROUP_DESCRIPTION = 'mt-1 text-sm text-fg-muted';
export const SETTINGS_SUBSECTION_TITLE = 'text-sm font-medium text-fg-strong';
export const SETTINGS_SUBSECTION_DESCRIPTION = 'mt-1 text-xs text-fg-muted';

/** Local mirror of the registry check, so a section can gate itself standalone. */
const meetsRequirement = (requires: SettingsRequirement, viewer: SettingsViewer): boolean => {
  if (requires.flag && !requires.flag()) return false;
  if (requires.orgAdmin && !viewer.isOrgAdmin) return false;
  if (requires.permission && viewer.accessRights?.[requires.permission] === false) return false;
  return true;
};

export const SettingsNotAuthorized: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <PiShieldWarningLight className="w-12 h-12 text-fg-muted mb-4" />
      <h3 className="text-lg font-medium text-fg-strong mb-2">{t('Admin access required')}</h3>
      <p className="text-sm text-fg-muted max-w-md">
        {message ||
          t('Only organization administrators can manage these settings. Contact your admin if you need changes.')}
      </p>
    </div>
  );
};

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  description,
  saveMode = 'explicit',
  isLoading = false,
  loadingVariant = 'form',
  dirty = false,
  pendingCount,
  isSaving = false,
  onSave,
  onDiscard,
  saveLabel,
  footerLeading,
  actions,
  requires,
  viewer,
  noGrid = true,
  gridCols = 1,
  fill = false,
  className,
  children,
}) => {
  const { t } = useTranslation();

  // "Saved" appears when a dirty section stops being dirty - which is what an
  // instant-save control does on every successful write.
  const [showSaved, setShowSaved] = useState(false);
  const wasDirty = useRef(dirty);
  useEffect(() => {
    const justSaved = wasDirty.current && !dirty;
    wasDirty.current = dirty;
    if (!justSaved) return undefined;
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), SAVED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [dirty]);

  if (requires && viewer && !meetsRequirement(requires, viewer)) {
    return (
      <GlassCard className={className}>
        <SettingsNotAuthorized />
      </GlassCard>
    );
  }

  const savedBadge = showSaved ? (
    <span className="flex items-center gap-1 text-xs text-success-600 dark:text-success-400">
      <PiCheckCircleLight className="w-4 h-4" />
      {t('Saved')}
    </span>
  ) : null;

  const footer = () => {
    if (isLoading) return null;
    if (saveMode === 'instant') {
      return (
        <p className="pt-4 text-xs text-fg-muted">{t('Changes are saved as you make them.')}</p>
      );
    }
    if (!onSave) return null;
    if (saveMode === 'batch') {
      return (
        <FormActions
          divider
          leading={
            footerLeading || (
              <span className="text-xs text-fg-muted">
                {dirty
                  ? t('{{count}} unsaved change(s)', { count: pendingCount ?? 0 })
                  : t('No unsaved changes')}
              </span>
            )
          }
        >
          {onDiscard && (
            <Button variant="outline" size="small" onClick={onDiscard} disabled={!dirty || isSaving}>
              {t('Discard')}
            </Button>
          )}
          <Button
            variant="primary"
            size="small"
            onClick={onSave}
            disabled={!dirty || isSaving}
            isLoading={isSaving}
          >
            {saveLabel || t('Save changes')}
          </Button>
        </FormActions>
      );
    }
    return (
      <FormActions divider leading={footerLeading}>
        <Button
          variant="primary"
          size="small"
          onClick={onSave}
          disabled={!dirty || isSaving}
          isLoading={isSaving}
        >
          {saveLabel || t('Save')}
        </Button>
      </FormActions>
    );
  };

  const hasHeader = !!(title || description || savedBadge || actions);

  let body: React.ReactNode = isLoading ? <Skeleton variant={loadingVariant} /> : children;
  if (fill) body = <div className="flex min-h-0 min-w-0 flex-1 flex-col">{body}</div>;
  else if (noGrid) body = <div className="space-y-4">{body}</div>;
  else body = <div className={clsx('grid gap-4', GRID_COLS_CLASS[gridCols] || GRID_COLS_CLASS[1])}>{body}</div>;

  /*
    NO CARD. A section floats on the settings canvas: its heading is text, its
    fields and value wells carry the white (`bg-field` under the pane's
    `.surface-canvas`), and the page around them stays paper. The white box
    that used to wrap every section made each page read as one more
    container, and its header repeated the page's own.
  */
  return (
    <section
      // `pt-4 first:pt-0`: padding, not margin, so it adds to whatever gap the
      // parent stack uses instead of fighting it - groups need more air than
      // fields once there is no card edge between them.
      className={clsx('flex flex-col gap-4 pt-4 first:pt-0', fill && 'h-full min-h-0', className)}
      data-settings-save-mode={saveMode}
    >
      {hasHeader && (
        <header className="flex shrink-0 items-start justify-between gap-4">
          <div className="min-w-0">
            {title && <h2 className={SETTINGS_GROUP_TITLE}>{title}</h2>}
            {description && <p className={SETTINGS_GROUP_DESCRIPTION}>{description}</p>}
          </div>
          {(savedBadge || actions) && (
            <div className="flex shrink-0 items-center gap-3">
              {savedBadge}
              {actions}
            </div>
          )}
        </header>
      )}
      {body}
      {footer()}
    </section>
  );
};

/**
 * A titled block of fields inside a section. Unboxed on purpose: on the
 * settings canvas the fields themselves are the white blocks, so a white well
 * around them would just be the old card again, one level down.
 */
export const SettingsSubsection: React.FC<{
  title?: React.ReactNode;
  description?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, description, className, children }) => (
  <div className={clsx('space-y-3 [&+&]:pt-3', className)}>
    {(title || description) && (
      <div>
        {title && <h3 className={SETTINGS_SUBSECTION_TITLE}>{title}</h3>}
        {description && <p className={SETTINGS_SUBSECTION_DESCRIPTION}>{description}</p>}
      </div>
    )}
    {children}
  </div>
);

export default SettingsSection;
